// src/components/desk/matchBands.jsx — the shared evidence-band vocabulary (Q1, the client
// surface). Everything here was MOVED VERBATIM out of MatchMatrix.jsx (the band card: CELL,
// bandsOf, the value formatters, BandRow/GapRow/QuietRow/Band, the labels) and out of
// MatchSuggestions.jsx (the check-label helpers: CHECK_MARK, humanMatchKey, deltaLabel and
// the feature-vocab cache they read).
//
// WHY A THIRD FILE: MatchMatrix already imports from MatchSuggestions, so the client card in
// MatchSuggestions could not import the band card back from MatchMatrix without closing a
// module cycle. This module is a LEAF — it imports from neither of them (lib/, Icon and
// db/core only) — and both surfaces import the ONE definition of a check's mark, its label,
// its asked/actual pair and its band grammar from here (B273: two implementations in two
// components is the drift, not the shape).
//
// ⚠️ THE CAPTURE CONTROLS MOVED HERE TOO (round-2 §3, 2026-08-12). The note that stood here
// said they "deliberately did NOT move: which control a surface can offer is the surface's
// own policy" — and the policy half of that is still true and still lives at each call site.
// What changed is that the CLIENT card now has a fourth column (`ვისი ჩასაწერია`) that must
// be filled on every row, so it needs the same three owner doors and the same client door
// the matrix opens. A second implementation of `setListingPolicy`-behind-a-pill is exactly
// the drift B273 names, and the answer is the same one this file was created for: one
// definition in the leaf, both surfaces importing it. `canRecord` / who-may-write stays a
// PROP, decided by the caller.
//
// ⚠️ AND THEY DRAGGED THEIR OWN DEPENDENCIES IN. `ownerAnswerKind`, `TERM_CHIPS`,
// `NUM_TERM_CHIPS` and the three `STANCE_*` tables lived in MatchSuggestions.jsx; OwnerFill
// closes over all six, and matchBands may import from NEITHER surface (that is the whole
// reason it exists). So they moved here as well and MatchSuggestions re-exports them
// unchanged — IntakeTab and ManualPropertyModal keep the imports they already had.

import { useEffect, useState } from 'react'
import { t, tLog, tOr, getLanguage } from '../../lib/i18n.js'
import { Icon, ROLE_ICON } from '../Icon.jsx'
import { pill } from './primitives.jsx'
import {
  getFeatures, listRestrictionRules, getRestrictionVocab, listListingPolicies, getListingTerms,
  setListingPolicy, setAnswerDeclined, setPropertyFeature, amendListingTerms, commitIntakeEnvelope,
} from '../../db/core.js'
import { ruleSentence } from '../../lib/restrictionSentence.js'
import { sourceKindLabel } from '../../lib/labels.js'
// districtLeaf: the LEAF name only. The asked/actual cells are narrow and clip, and a
// full path ("თბილისი › ვაკე-საბურთალო › ვაკე") would truncate to the city — the one
// part that distinguishes nothing.
import { districtLeaf } from '../../lib/districtLabel.js'
import { DateInput } from '../SegmentedInput.jsx'
import { whoAnswers, ruleIdOf, clientGapOrder } from '../../lib/matchGaps.js'

// ─── The check-label helpers (moved from MatchSuggestions.jsx) ─────────────────────────
// TEXT marks (see TIER_ICON in MatchSuggestions): consumed by checkLabel/tip inside
// template literals.
export const CHECK_MARK = { pass: '✓', partial: '≈', fail: '✗', unknown: '?' }
// Scorer keys and delta kinds share the match_key_* i18n namespace ("district_mismatch" →
// "district doesn't match"); anything not in the dictionary degrades to underscores→spaces
// instead of leaking an engineer-facing token to agents (2026-07-13 UI audit).
// B573 rule 2 — THE TIER IS NOT PART OF THE NAME.
// The scorer prefixes feature checks by requirement tier: `must:<code>`, `mustnot:<code>`,
// `nice:<code>` (match-score.mjs). Only `must:` was ever stripped here, so a bonus feature
// rendered as "nice:lift present" — the colon survives the underscore replacement, so the
// row leaked a check key AND buried the tier inside the noun, where it reads as part of the
// feature's name rather than as how badly the client wants it.
// Exported because the CARD renders the tier as its own tag (`სასურველი`) beside the name;
// a caller that only wants the noun gets it from humanMatchKey as before.
export function checkTier(raw) {
  return /^(nice|mustnot|must):/.exec(String(raw || ''))?.[1] || null
}
export function bareCheckKey(raw) {
  return String(raw || '').replace(/^(?:nice|mustnot|must):/, '')
}

// ⚠️ Deduped, and deliberately not silent. Rule 2 asks for an unmapped key to LOG, because
// the degraded "underscores → spaces" form below is indistinguishable from a real label at a
// glance: `land_area_min` reads as "land area min" and looks translated. Nothing was ever
// counting how often that happened, so the dictionary gap was unmeasurable.
const _loggedMatchKeys = new Set()
export function logUnmappedMatchKey(key) {
  if (!key || _loggedMatchKeys.has(key)) return
  _loggedMatchKeys.add(key)
  console.warn(`[i18n] no match_key_${key} — check label degraded (B573 rule 2)`)
}

// The DB vocabulary, consulted FIRST (owner ruling 2026-08-08: the noun for a subject is
// `feature.name_ka` — a row nobody has to translate — never a JS copy map). Session
// promise-cache, same pattern as the district dictionary below; getFeatures() itself
// caches, so this only skips the async hop on re-render. Consumers re-render via the
// component-level effect that awaits it.
let _featVocab = null
let _featVocabPromise = null
export function ensureFeatureVocab() {
  _featVocabPromise ??= getFeatures()
    .then(list => { _featVocab = new Map((list || []).map(f => [f.code, f])); return _featVocab })
    .catch(() => { _featVocabPromise = null; return null })
  return _featVocabPromise
}
export function humanMatchKey(raw) {
  if (!raw) return ''
  const key = bareCheckKey(raw)
  // policy:<subject> checks (owner stances vs household profile, 0080) — "policy:pol_family"
  // must read "owner rule on family unknown", not leak the code (2026-07-13 owner feedback).
  if (key.startsWith('policy:')) {
    const pol = _featVocab?.get(key.slice(7))
    const subject = pol
      ? ((getLanguage() === 'ka' ? pol.name_ka : pol.name_en) || pol.name_ka || pol.name_en)
      : key.slice(7).replace(/^pol_/, '').replace(/^household_/, '').replace(/_/g, ' ')
    return tLog('match_key_policy_wrap', { subject })
  }
  // Vocabulary first (B573 rule 2's order): `must:elevator` is named by its DB row —
  // ლიფტი — and only a key with no row falls through to the i18n dictionary.
  const feat = _featVocab?.get(key)
  if (feat) {
    const name = (getLanguage() === 'ka' ? feat.name_ka : feat.name_en) || feat.name_ka || feat.name_en
    if (name) return name
  }
  const hit = t(`match_key_${key}`)
  if (hit !== `match_key_${key}`) return hit
  // ⚠️ STILL DEGRADES RATHER THAN BLANKING, and that is a deliberate scope limit.
  // Rule 2's "an unmapped key renders an EMPTY label slot" is about the CARD's four-column
  // row, where the label has its own cell and an empty one is honest. This function is also
  // interpolated into delta chips — `${humanMatchKey(d.kind)} $10,000` — where returning ''
  // would produce a chip reading " $10,000": a number with nothing saying what it is, which
  // is worse than a degraded noun. The card gets the empty slot via cardCheckLabel();
  // everyone else keeps the fallback and the gap is now LOGGED for both.
  logUnmappedMatchKey(key)
  return key.replace(/_/g, ' ')
}
export function deltaLabel(d) {
  // A move-in gap's whole point is WHO concedes and by how much — "move-in gap" would
  // throw both away (scorer v0.9.0). Rendered as the ask, because a match is only useful
  // if it carries the next action.
  if (d.kind === 'move_in_gap') {
    return tLog(d.concession === 'owner' ? 'match_move_in_owner_holds' : 'match_move_in_tenant_waits',
      { n: d.gap_days })
  }
  return `${humanMatchKey(d.kind)}${d.amount_usd != null ? ` $${Number(d.amount_usd).toLocaleString()}` : ''}${d.feature ? ` ${humanMatchKey(d.feature)}` : ''}`
}

// ── Cell vocabulary (02-spec.md "States") ────────────────────────────────────────────────
// Five distinct states, four of them reachable from a check and one from its ABSENCE. The
// absence case is the one that is easy to get wrong: a client who never asked about a
// condition has no check for it, and rendering that as ✗ ("the property fails it") or as ?
// ("nobody asked the owner") would both be lies. It is a dash: not asked for.
// RULE 8 (redline): every mark is in the legend, and a mark that is not in the legend does
// not ship. `≈` appeared on four rows and was defined nowhere — the reviewer's ruling was to
// DROP it rather than name it, because "weakly evidenced" does not compress into two Georgian
// words without sounding robotic. A partial now renders as ✓ and the `ერთი წყარო`
// qualifier appears on expand, which is the grammar the panel already had.
export const CELL = {
  pass:    { mark: '✓', cls: 'text-emerald-500',                 titleKey: 'mv2_legend_met' },
  // ⚠️ `≈`, NOT `✓`. `bandsOf` files a partial under **არ ემთხვევა**, so a green tick here
  // put a row that reads "met" inside the band headed "does not match" — reported from
  // production 2026-08-13 alongside the price ruling. The chip line has always used `≈` for
  // this status (CHECK_MARK above); the band was the odd one out, and one status may not
  // read as two different verdicts on two surfaces.
  partial: { mark: '≈', cls: 'text-amber-500',                   titleKey: 'mv2_one_source_verify' },
  fail:    { mark: '✗', cls: 'text-red-400',                     titleKey: 'mv2_legend_missed' },
  // A HARD fail is a binding condition — the intent gate, the category gate. Muted rather
  // than red on purpose: red invites "can we move this?", and this one cannot be moved.
  hard:    { mark: '✗', cls: 'text-foreground/80',               titleKey: 'mv2_legend_fixed' },
  owner:   { mark: '?', cls: 'text-amber-500',                   titleKey: 'mv2_nobody_asked_owner' },
  client:  { mark: '?', cls: 'text-violet-500',                  titleKey: 'mv2_client_answers' },
  clientFail: { mark: '✗', cls: 'text-violet-500',              titleKey: 'mv2_legend_client_cond' },
  absent:  { mark: '–', cls: 'text-muted-foreground/50',         titleKey: 'mv2_legend_never_asked' },
}
// Which of the five a check lands in. An unknown splits by WHO can answer it, because that
// is the only thing an agent does with it — dial the owner or dial the client.
// ⚠️ The split now reads `delta.actions` (whoAnswers, src/lib/matchGaps.js), which is the
// SCORER'S OWN declaration of who is being asked. It used to regex the English `detail`, and
// that sent every `household_gap` — "the owner forbids pets, this client never said whether
// they have one" — to the OWNER strip, so the violet tier stayed empty on exactly the rows
// it exists for. See matchGaps.js's header for the full account; do not reintroduce a regex.
export function cellOf(check) {
  if (!check) return CELL.absent
  // A fail on the CLIENT's own requirement is violet, not red: red says "there is a lever
  // here", and on the client's own condition the lever belongs to the client, not the owner.
  if (check.status === 'fail') {
    if (check.hard) return CELL.hard
    return whoAnswers(check) === 'client' ? CELL.clientFail : CELL.fail
  }
  if (check.status === 'unknown') return whoAnswers(check) === 'client' ? CELL.client : CELL.owner
  return CELL[check.status] || CELL.absent
}

// ── The expanded card's three bands ──────────────────────────────────────────────────────
// Only checks with WEIGHT are listed: the gates (intent, category) carry weight 0 and are
// pass/fail plumbing, not something an agent negotiates — except when one is the hard fail
// that rejected the row, which by construction never reaches this panel.
export function bandsOf(checks, blockers) {
  const blocking = new Set(blockers || [])
  const live = (checks || []).filter(c => (c.weight ?? 0) > 0 || c.status === 'fail')
  const why = live.filter(c => c.status === 'pass')
  const mismatch = live.filter(c => c.status === 'fail' || c.status === 'partial')
  const unknown = live.filter(c => c.status === 'unknown')
  const isClient = c => whoAnswers(c) === 'client'
  // 05-v2-answers Q2 fixed the client-side order (move-in → occupants → pets → children);
  // the scorer emits in its own evaluation order, which is not that. Sorting here rather
  // than at each render keeps the summary sentence counting the same list the band shows.
  const byDesign = (a, b) => clientGapOrder(a) - clientGapOrder(b)
  return {
    why,
    mismatch,
    // 05-v2-answers Q2: two tiers, same band, hard separation. "Blocking" means the check
    // is in the classifier's own `blockers` array — answering it can change this client's
    // verdict. "Merely unknown" means nobody asked and nothing hangs on it; it is offered
    // quietly and, critically, is NEVER counted in the "to fill 3 + 2" numbers. 95% of
    // clients have no deposit check at all, so counting mere absence would put a number on
    // nearly every card that unlocks nothing.
    ownerBlocking: unknown.filter(c => !isClient(c) && blocking.has(c.key)),
    clientBlocking: unknown.filter(c => isClient(c) && blocking.has(c.key)).sort(byDesign),
    quiet: unknown.filter(c => !blocking.has(c.key)).sort(byDesign),
  }
}

// ── The band row grid (02-spec.md "Row grid") ────────────────────────────────────────────
// `14px | 1.15fr | 1fr | 1fr | auto` — mark, condition, and then TWO value columns plus a
// trailing slot. Which two depends on the band, and that is the whole point of the redesign:
// the first build rendered one label and one delta sentence, so a row said *ფასი* and left
// the agent to open the client card to learn what they asked and the property card to learn
// what it costs. The design's answer is to put both on the row —
//     რატომ ემთხვევა / არ ემთხვევა:  პირობა | კლიენტი ითხოვს | ობიექტზე | წყარო
//     შესავსები:                     პირობა | ვინ პასუხობს   | დღეს რა ვიცით | control
// — which is also why the two bands cannot share one header.
//
// ── Q5 (mobile): three text columns become TWO ───────────────────────────────────────────
// The redline's phone rule is "mark · label · value" — four text tracks inside a 375px card
// truncate to two syllables each, which is worse than not showing the column. So the grid
// carries a THREE-track mobile default and the existing five-track grid moves behind `sm:`.
//
// ⚠️ THE DESKTOP MUST NOT MOVE. `sm:` restores the exact track list this line has always had,
// and every cell that changes below either folds (via a `sm:contents` wrapper, which vanishes
// from layout at ≥sm so the same five children land in the same five tracks) or is hidden by
// `hidden sm:block` (a grid item is blockified anyway, so `block` is what an inline span
// already computes to here). Nothing in this file is allowed to alter the ≥640px render.
export const ROW_GRID = 'grid grid-cols-[14px_minmax(0,1fr)_auto] gap-x-2 '
  + 'sm:grid-cols-[14px_minmax(0,1.15fr)_minmax(0,1fr)_minmax(0,1fr)_auto]'

// ── Where the two value columns come from, and where they DON'T ─────────────────────────
// Everything below reads a row that already exists — the scorer's own `delta`, the demand
// digest the endpoint serialises, the listing's recorded stances/rules/terms. Nothing is
// derived from prose and nothing is invented: a column with no backing renders EMPTY.
//
// ✅ THE DURABLE FIX LANDED (2026-08-08, scorer 0.14.0 + endpoint). The note that stood here
// said: "the scorer emits `asked`/`actual` on 6 of its ~115 checks; the durable fix is to
// carry a display pair on the check itself — one field at the producer, not a lookup table
// per consumer." That is now built. Every value-bearing check (intent, category, price,
// rooms, bedrooms, area, land_area, power_capacity, floor, location) carries `asked`/`actual`
// from the scorer, and `source` from the endpoint (B573 steps 1–2).
//
// So the producer's pair WINS below, and the per-consumer lookups that follow it survive
// only as the fallback for checks the scorer does not yet cover (policy stances, terms,
// rules — all read from the listing's own rows). Do not extend those tables: add the pair at
// the producer instead, which is the whole point of the seam.
//
// ⚠️ The blank cell is STILL the right answer where no pair exists. A column with no backing
// renders empty; nothing here guesses.
export const fmtRange = ([min, max]) =>
  min != null && max != null ? (min === max ? `${min}` : `${min}–${max}`)
    : min != null ? `${min}+` : max != null ? `≤ ${max}` : ''
export const fmtMoney = (n) => (n == null ? '' : `$${Math.round(Number(n)).toLocaleString('en-US')}`)

// Keys whose values are money and must carry a currency mark. A price rendered as a bare
// number beside a budget rendered as one is two figures an agent has to guess the unit of.
export const MONEY_KEYS = new Set(['price', 'appraisal', 'deposit'])

// Renders scorer 0.14.0's STRUCTURED asked/actual. The producer deliberately never formats
// (its own comment: "structured, never formatted — the card renders '≤$800' / '$950' from
// these"), so every shape it can emit has to land somewhere sensible here:
//   scalar            → itself
//   { min, max }      → a range, and a money range when the key says so
//   array             → joined, because location's `actual` names BOTH the poster's label and
//                       the map pin rather than picking a winner (0479) — showing one would
//                       hide exactly the disagreement that block exists to surface
//   object            → its own display-ish field if it has one, else nothing. NEVER
//                       String(obj): "[object Object]" in a value column reads as real data.
export function fmtValue(v, { money = false } = {}) {
  if (v == null) return ''
  if (Array.isArray(v)) return v.map(x => fmtValue(x, { money })).filter(Boolean).join(' · ')
  if (typeof v === 'object') {
    if ('min' in v || 'max' in v) {
      // ⚠️ MUST return null for an absent bound, not ''. fmtRange tests `!= null`, and an
      // empty string passes that test — so a one-sided budget rendered as ['', '$750'] took
      // the two-sided branch and printed "–$750" instead of "≤ $750". Caught by rendering;
      // a build cannot see it and neither can a test that only feeds two-sided ranges.
      const f = money ? (x) => (x == null ? null : fmtMoney(x)) : (x) => (x == null ? null : String(x))
      return fmtRange([f(v.min), f(v.max)])
    }
    // ⚠️ `v.code` REMOVED from this chain (2026-08-08). It would render a raw district
    // code: `location.actual` is {label, geo} of CODES, so this generic fallback printed
    // "TBS_VAKE" on screen — precisely the failure districtLabel.js exists to prevent ("no
    // location beats an internal identifier"; a real client was sent a link reading
    // TBS_VEDZISI). Location is intercepted before this line now; the chain is narrowed so
    // the NEXT shape cannot fall through to a code.
    return v.name ?? v.value ?? ''
  }
  if (typeof v === 'boolean') return t(v ? 'mv2_yes' : 'mv2_no')
  return money ? fmtMoney(v) : String(v)
}
export const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v)

// What the CLIENT asked for on this condition.
//
// 📌 SCORER 0.14.0 (B573) IS NOW THE FIRST SOURCE, and that is the right way round: the
// producer states the pair structurally on the CHECK, and this function's job shrank to
// FORMATTING it. Everything below the first branch is fallback for a payload from an older
// scorer, or for checks 0.14.0 has not reached — kept because the panel must not blank a
// column the moment a server lags behind a deploy, and because the header note above still
// applies to whatever remains unfilled.
//
// ⚠️ Do NOT extend the fallbacks. Each one is a second implementation of a fact the scorer
// can state, which is the drift B273 names; if a column is empty, fill `asked`/`actual` at
// the producer instead of adding a case here.
export function askedOf(check, m) {
  // Scorer 0.14.0 (B573) is the first source. Two shapes are handled BEFORE the generic
  // formatter because it cannot express them without losing the point of the row.
  const a = check.asked
  if (isObj(a) && check.key === 'floor') {
    // A floor requirement is not only a band: "not the ground floor" has no numeric bound, so
    // fmtValue's {min,max} branch returns '' and the cell would read as no preference.
    // ⚠️ Owner ruling 2026-08-08: Georgian is never invented in JS. Both keys are EXISTING
    // reviewed rows reused from the criteria panel, which also means the card and that panel
    // word the same requirement identically.
    const parts = [fmtValue(a), a.not_first ? t('desk_criteria_floor_not_first') : '',
                   a.not_last ? t('desk_criteria_floor_not_last') : ''].filter(Boolean)
    return parts.join(', ')
  }
  if (isObj(a) && check.key === 'location') {
    // ⚠️ CODES NEVER REACH THE SCREEN. The endpoint resolves the codes this check uses into
    // `check.districts` (code → path); anything unresolved renders nothing, never an identifier.
    const lang = getLanguage()
    const codes = (a.must && a.must.length) ? a.must : (a.desirable || [])
    const names = codes.map(cd => districtLeaf(check.districts?.[cd], lang)).filter(Boolean)
    if (names.length) return names.join(', ')
    return a.polygon ? t('desk_crit_search_area') : ''
  }
  if (a != null) return fmtValue(a, { money: MONEY_KEYS.has(check.key) })
  const d = check.delta || {}
  if (Array.isArray(d.asked)) return fmtRange(d.asked)
  if (d.asked != null) return String(d.asked)
  const dem = m?.demand || {}
  if (check.key === 'price') {
    const b = dem.budget_usd || {}
    if (b.min == null && b.max == null) return ''
    return b.max != null && b.min == null ? `≤ ${fmtMoney(b.max)}`
      : b.min != null && b.max == null ? `${fmtMoney(b.min)}+`
      : `${fmtMoney(b.min)}–${fmtMoney(b.max)}`
  }
  if (check.key === 'rooms') return fmtRange([dem.rooms?.min, dem.rooms?.max])
  return ''
}

// What the PROPERTY carries. Recorded stances and terms are read from the listing's own
// rows, so the column says the same thing the terms panel does — one source, two surfaces.
export function actualOf(check, ans) {
  // Scorer 0.14.0 (B573) first, same rule as askedOf: the producer states the value, this
  // only formats it. The branches below are fallback, not a second source.
  const v = check.actual
  if (isObj(v) && check.key === 'location') {
    // Location names BOTH evidence sources (0479). Showing one hides the disagreement the
    // agent is being asked to resolve, so a conflict prints both with the pin marked.
    const lang = getLanguage()
    const label = v.label ? districtLeaf(check.districts?.[v.label], lang) : ''
    const geo = v.geo ? districtLeaf(check.districts?.[v.geo], lang) : ''
    if (label && geo && v.label !== v.geo) return `${label} / 📍${geo}`
    return label || geo || ''
  }
  if (v != null) return fmtValue(v, { money: MONEY_KEYS.has(check.key) })
  const d = check.delta || {}
  if (d.actual != null && typeof d.actual !== 'object') return String(d.actual)
  const subject = /^policy:(.+)$/.exec(check.key)?.[1]
  if (subject) {
    const p = ans?.policyBySubject?.get(subject)
    return p?.stance ? t(`desk_stance_${p.stance}`) : ''
  }
  const terms = ans?.terms
  if (terms) {
    if (check.key === 'pets' && terms.pets_allowed != null)
      return t(terms.pets_allowed ? 'desk_term_allowed' : 'desk_term_not_allowed')
    if (check.key === 'children' && terms.children_allowed != null)
      return t(terms.children_allowed ? 'desk_term_allowed' : 'desk_term_not_allowed')
    if (check.key === 'occupants' && terms.max_occupants != null) return String(terms.max_occupants)
  }
  return ''
}

// Provenance. `sourceKindLabel` is the shared dictionary (labels.js) — never a raw
// `source_kind` token, which is the "speaks database" complaint in its purest form.
// ⚠️ The design prints a NAME ("05.08 · გიორგი"). `listing_policy` / `restriction_rule`
// serialise `recorded_by_agent_id` and no name, so this renders the SOURCE KIND and the
// date. Printing a name would mean joining the roster server-side; showing the wrong one, or
// "გიორგი" for everybody, would be worse than showing the kind.
const shortDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(+d) ? '' : `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`
}
export function provenanceOf(row) {
  if (!row) return ''
  const kind = row.source_kind ? sourceKindLabel(row.source_kind) : ''
  const when = shortDate(row.last_seen_at || row.recorded_at || row.updated_at)
  return [kind, when].filter(Boolean).join(' · ')
}
export function sourceOf(check, ans) {
  // 0.14.0 (B573 step 2): the endpoint's own provenance, when it has any. `portal` is a
  // CHANNEL, not strictly a site — see the caveat in core_match.js — so a repost is marked
  // rather than being passed off as a separate portal.
  const sc = check.source
  if (sc && sc.portal) {
    const when = shortDate(sc.at)
    const mark = sc.source_kind === 'scrape_reshare' ? ' ↻' : ''
    const dispute = sc.disputed ? ' ⚠' : ''
    // Round-2: a fact whose ONLY source is a withdrawn ad must say so, the same way the
    // property side refuses to show photos that only exist on a pulled listing. The fact
    // may still be true — it is not struck through — but "the ad this came from is gone"
    // changes what an agent should promise a client, so it is never left implicit.
    const withdrawn = sc.withdrawn && (sc.source_count ?? 1) <= 1 ? ` · ${t('msug_source_withdrawn')}` : ''
    return `${sc.portal}${mark}${when ? ` · ${when}` : ''}${dispute}${withdrawn}`
  }
  const rid = ruleIdOf(check.key)
  if (rid) return provenanceOf(ans?.rulesById?.get(rid))
  const subject = /^policy:(.+)$/.exec(check.key)?.[1]
  if (subject) return provenanceOf(ans?.policyBySubject?.get(subject))
  if (ans?.terms && ['pets', 'children', 'occupants', 'deposit', 'prepayment'].includes(check.key))
    return provenanceOf(ans.terms)
  return ''
}

// One line of the evidence bands (რატომ ემთხვევა / არ ემთხვევა). `detail` rides as the
// tooltip only (English; see the header), and `delta` still supplies the sentence with the
// number in it — but as the trailing slot rather than as the row's only content.
export function BandRow({ check, ans, m, lever = null }) {
  const cell = cellOf(check)
  const asked = askedOf(check, m)
  const actual = actualOf(check, ans)
  const delta = check.delta ? deltaLabel(check.delta) : ''
  return (
    <div className={`${ROW_GRID} items-baseline px-2.5 py-[2px] border-b border-border/40 last:border-0`}>
      <span className={`text-xs ${cell.cls}`} title={cell.titleKey ? t(cell.titleKey) : undefined}>{cell.mark}</span>
      {/* Q5 fold. On a phone the ASKED value rides INSIDE the label cell ("ფასი ≤ $750") and
          the ACTUAL keeps the right-hand cell — the client's ask and the property's answer are
          the two halves of the row, and the ask is the half that reads as part of the
          condition. `sm:contents` dissolves this wrapper at ≥640px, so desktop still receives
          CheckLabel and the asked cell as two separate grid items in tracks 2 and 3. */}
      {/* 🚨 `flex` IS LOAD-BEARING, not decoration (owner screenshot, 2026-08-19: the
          condition label painted straight through the value column — "კონდიციო**ნირების**
          ნტ… air conditioner"). This wrapper was a bare `min-w-0` span. As a GRID ITEM it
          is blockified and `min-w-0` does bite, so the wrapper itself shrank correctly —
          but its CHILDREN stayed `display:inline`, and `truncate` (overflow:hidden +
          text-overflow:ellipsis) has no effect whatsoever on a non-replaced inline box.
          So CheckLabel measured its full natural width, ignored the wrapper's bounds and
          overflowed to the right, landing on top of the actual-value cell in track 3.
          Making the wrapper a flex container blockifies its children, which is what finally
          turns their existing `truncate` on. Nothing else changed — the classes were always
          correct, they simply had no box to apply to.
          ⚠️ `sm:contents` must stay LAST-word: at ≥640px the wrapper dissolves and desktop
          receives CheckLabel and the asked cell as two separate grid items, exactly as
          before. Do not replace it with a media-queried flex. */}
      <span className="min-w-0 flex items-baseline sm:contents">
        <CheckLabel check={check} ans={ans} />
        {/* The ask is short ("≤ $750", "2–3") and is the half that must survive: it is the
            CONDITION. So it never shrinks — the label absorbs the squeeze. */}
        {asked ? <span className="ml-1 shrink-0 whitespace-nowrap text-xs text-muted-foreground sm:hidden">{asked}</span> : null}
      </span>
      <span className="hidden text-xs text-muted-foreground truncate whitespace-nowrap sm:block" title={asked || undefined}>{asked}</span>
      <span className="text-xs text-foreground/90 truncate whitespace-nowrap" title={actual || delta || undefined}>
        {actual || delta}
      </span>
      {/* The lever (02-spec: "✗ row + lever button"). Only the mismatch band passes one, and
          only when the row has somewhere real to go — a button that opens nothing is the
          2026-08-06 lock-with-no-action defect wearing a different label.
          Q5: per-row source is a DESK affordance — on a phone the source collapses to one line
          in the card header instead (client surface), so this fifth track is desktop-only. */}
      <span className="hidden justify-self-end sm:block">{lever}</span>
    </div>
  )
}

// The quiet tier's row: neutral `·`, never the `?` the blocking tier uses, because `?` is a
// question someone has to be asked and this one is not. Bare `＋`, no leverage number.
export function QuietRow({ check, ans, control }) {
  return (
    <div className={`${ROW_GRID} items-baseline px-2.5 py-[2px] border-b border-border/40 last:border-0`}>
      <span className="text-xs text-muted-foreground" title={t('mv2_blocks_nothing')}>·</span>
      {/* Q5 fold, same shape as BandRow: on a phone the role rides inside the label cell and
          `sm:contents` hands desktop back its two separate tracks.
          `flex` for the same reason BandRow needs it — see the note there. */}
      <span className="min-w-0 flex items-baseline sm:contents">
        <CheckLabel check={check} ans={ans} />
        <span className="ml-1 shrink-0 text-xs text-muted-foreground truncate sm:ml-0">
          {/* The ROLE, not the sentence. The column header already asks "ვინ პასუხობს", so
              "მეპატრონე პასუხობს" repeats the question inside its own answer and truncated to
              "მეპატრონ…" in a 1fr column — the mock prints the bare noun for exactly that
              reason. */}
          <RoleTag role={whoAnswers(check)} />
        </span>
      </span>
      {/* Q5: the value and the control share the third (auto) track on a phone; at ≥sm they
          split back into tracks 4 and 5. A QuietRow's control is a write door on the desk
          surface, so it is FOLDED rather than hidden — unlike BandRow's read-only source. */}
      <span className="min-w-0 flex items-center justify-end gap-1.5 sm:contents">
        <span className="text-xs text-muted-foreground/70 truncate" title={check.detail || undefined}>
          {check.delta ? deltaLabel(check.delta) : ''}
        </span>
        <span className="justify-self-end">{control}</span>
      </span>
    </div>
  )
}

// A row in შესავსები. Three facts and a control: the question, who holds the answer, what we
// already know, and the way to record it. Copy rule (02-spec §Copy): the unknown cell names
// the PERSON, never "უცნობია".
//
// Q5 (mobile): this row STACKS instead of gridding — the question on its own line, then
// `who answers · what we know` muted underneath, then the control on its own line. It is the
// row an agent reads out loud mid-call, so the question must not be a truncated third of a
// 375px line. Same component, responsive classes only: a second mobile component would be
// the two-implementations drift this file exists to prevent (B273).
export function GapRow({ check, ans, tone, known, leverage = 0, control }) {
  return (
    <div className={`${ROW_GRID} items-center px-2.5 py-[3px] border-b border-border/40 last:border-0`}>
      <span className={`text-xs ${tone}`}>?</span>
      {/* col-span-2 → the question owns the rest of the mobile row; `sm:col-span-1` puts it
          back in its single desktop track. */}
      <CheckLabel check={check} ans={ans} className="col-span-2 sm:col-span-1" />
      {/* Line 2 on mobile. `col-start-2` under the mark, and auto-placement drops it to the
          next row because the first row is full. `sm:contents` dissolves it at ≥640px so the
          role and the known value are the same two grid items desktop has always had. */}
      <span className="col-start-2 col-span-2 min-w-0 flex items-baseline gap-1 sm:contents">
        <span className="text-xs text-muted-foreground truncate">
          {/* The ROLE, not the sentence. The column header already asks "ვინ პასუხობს", so
              "მეპატრონე პასუხობს" repeats the question inside its own answer and truncated to
              "მეპატრონ…" in a 1fr column — the mock prints the bare noun for exactly that
              reason. */}
          <RoleTag role={whoAnswers(check)} />
        </span>
        {known ? <span className="text-xs text-muted-foreground/70 sm:hidden">·</span> : null}
        <span className="text-xs text-muted-foreground truncate" title={known || undefined}>{known}</span>
      </span>
      {/* Line 3 on mobile: the control gets its own full-width line rather than a 40px
          trailing slot — the whole point of Q5's hit-target rule.
          🚨 `justify-self-start` MADE THAT LINE A LIE (owner screenshot, 2026-08-19: the
          last answer pill ran off the right edge of the card, showing one stray glyph).
          `justify-self-start` sizes a grid item to its CONTENT, so this box was shrink-to-fit
          — and AnswerPills' `w-full` then resolved against a box that was itself measuring
          the pills. The strip could grow past the column and nothing clipped or scrolled it.
          `justify-self-stretch` makes the line genuinely full-width, which is what `w-full`
          was always written against. Desktop keeps `justify-self-end` (a compact trailing
          control beside a wide row) — that side never had the bug. */}
      <span className="col-start-2 col-span-2 min-w-0 justify-self-stretch flex items-center gap-1.5 sm:col-start-auto sm:col-span-1 sm:justify-self-end">
        {leverage > 1 && (
          <span className={`text-xs tabular-nums whitespace-nowrap ${tone}`}>{tLog('mv2_unlocks', { n: leverage })}</span>
        )}
        {control}
      </span>
    </div>
  )
}

// An answered subject, kept on screen instead of vanishing (02-spec: "✓ + provenance —
// answered and recorded; never asked again"). A gap that simply disappears when filled gives
// the agent no way to see what was recorded, or to notice that it was recorded wrongly.
export function AnsweredRow({ label, value, source }) {
  return (
    <div className={`${ROW_GRID} items-baseline px-2.5 py-[2px] border-b border-border/40 last:border-0`}>
      <span className="text-xs text-emerald-500">✓</span>
      {/* Q5 fold, same rule as BandRow/QuietRow — including the `flex` that makes the
          children's `truncate` real. See the note on BandRow. */}
      <span className="min-w-0 flex items-baseline sm:contents">
        <span className="text-xs text-foreground truncate">{label}</span>
        <span className="ml-1 shrink-0 text-xs text-muted-foreground truncate sm:ml-0"><RoleTag role="owner" /></span>
      </span>
      <span className="text-xs text-foreground font-medium truncate">{value}</span>
      <span className="hidden text-xs text-muted-foreground/70 whitespace-nowrap justify-self-end sm:block">{source}</span>
    </div>
  )
}

// ⚠️ `count` is what the agent reads and repeats, so it counts ONLY what is blocking —
// 05-v2-answers Q2, and it is the whole reason the quiet tier exists as a separate thing.
// `show` is a separate argument for exactly that reason: a band with nothing blocking but
// something worth recording still renders, headed by a count of zero blockers rather than a
// count inflated by rows that unlock nothing. (First build summed all three lists and put
// "შესავსები · 4" above a summary sentence that said 3 — the surface contradicting itself.)
//
// `headers` names the band's own two value columns. They are not shared between bands
// because the columns are not the same columns — see ROW_GRID.
//
// `className` (Q5): the ONE hook the client card needs to reorder a band on mobile
// (`order-first sm:order-none` on შესავსები). Defaults to '' so no existing call site moves.
export function Band({ labelKey, count, show = null, tint, headers = null, className = '', children }) {
  if (!(show ?? count)) return null
  return (
    <div className={`mt-1.5 rounded-lg border border-border/60 overflow-hidden ${className}`.trim()}>
      <div className={`${ROW_GRID} items-baseline px-2.5 py-1 text-[10px] uppercase tracking-wide ${tint}`}>
        <span />
        {/* Q5: the header labels fold exactly the way the CELLS below them fold, or the
            headers stop naming the columns they sit over. Same `sm:contents` dissolve —
            and the same `flex`, for the same reason: the owner's screenshot caught
            "შესავსები · 5  ვინ პასუხობს" running straight through "დღეს რა ვიცით"
            because these two inline spans could not be clipped. See BandRow. */}
        <span className="min-w-0 flex items-baseline sm:contents">
          <span className="truncate">{t(labelKey)} <span className="opacity-70">· {count}</span></span>
          <span className="ml-1 shrink-0 truncate opacity-70 sm:ml-0">{headers?.[0] ? t(headers[0]) : ''}</span>
        </span>
        <span className="truncate opacity-70">{headers?.[1] ? t(headers[1]) : ''}</span>
        <span className="hidden opacity-70 justify-self-end whitespace-nowrap sm:block">{headers?.[2] ? t(headers[2]) : ''}</span>
      </div>
      {children}
    </div>
  )
}

// The "what to do" sentence. A BLOCK element, never a flex item (02-spec layout hygiene —
// as a flex item it competes for width with the row's meta and truncates the instruction).
// Four shapes, chosen by what is actually open, so it reads as an instruction rather than a
// tally. The generic form carries every number; the three specific ones fire when exactly
// one kind of work is left, which is when an agent can act without reading further.
export function summarySentence(b) {
  const met = b.why.length
  const missed = b.mismatch.length
  const ownerGaps = b.ownerBlocking.length
  const clientGaps = b.clientBlocking.length
  const total = met + missed + ownerGaps + clientGaps
  if (!total) return null
  if (missed > 0) return tLog('mv2_summary_mismatch', { n: missed })
  if (!ownerGaps && !clientGaps) return t('mv2_summary_all_ok')
  if (ownerGaps && !clientGaps) return t('mv2_summary_owner_only')
  if (clientGaps && !ownerGaps) return tLog('mv2_summary_client_only', { n: clientGaps })
  return tLog('mv2_summary', { total, met, missed, ownerGaps, clientGaps })
}

// ── Condition labels ─────────────────────────────────────────────────────────────────────
// humanMatchKey() is the shared vocabulary and stays the fallback for every ordinary check.
// But it renders a `policy:pol_foreigners` check as the SENTENCE "მესაკუთრის წესი —
// foreigners — უცნობია", and that is wrong twice over as a matrix column:
//   • it leaks the raw English subject code, which is exactly the "speaks database"
//     complaint (2026-07-13 UI audit) — visible here in a way it never was inside a chip;
//   • it bakes "უცნობია" (unknown) into the LABEL, so a ✓ cell would sit under a header
//     asserting the opposite. In a chip the status and the text arrived together; in a
//     matrix the cell owns the status and the header must own only the subject.
// The subject's display name already exists as the `pol_*` feature row's name_ka — one
// vocabulary, and the same one whoIsItFor.js labels its pills from. No second taxonomy.
//
// ⚠️ IT MUST COVER THE AMENITY KEYS AS WELL, and the first version did not — which shipped
// `elevator` as a column header and `air conditioner` / `heat central` / `furnished` /
// `bedrooms off by` into the bands of an all-Georgian panel. humanMatchKey falls through to
// `key.replace(/_/g,' ')` for anything it has no i18n row for, so an unmapped code renders
// as its raw English self and looks like a deliberate label rather than a miss. The feature
// vocabulary already names every one of these; the bug was only ever the `policy:` guard.
//
// ⚠️ AND IT MUST COVER `rule:<uuid>` KEYS, which the first version also did not — the
// restriction-rules block (v0.12.0) keys every check `rule:${rule.id}`, so the panel printed
// `rule:45d06c79-d21c-40df-8fab-135ff3a24d80` as a condition name, twice, inside an
// all-Georgian card. The rule's own sentence belongs there and the app already builds it:
// `ruleSentence()` is the SAME generator the capture panel previews with, so the row an
// agent reads here is word-for-word the rule they recorded. A rule that has not loaded (or
// belongs to another listing) degrades to the generic "მესაკუთრის წესი", never to a uuid.
//
// `ans` is the answer context — vocab plus the listing's own rules/stances/terms. It is
// passed as one object rather than five props because every row needs all of it.
export function conditionLabel(key, ans) {
  const rid = ruleIdOf(key)
  if (rid) {
    const rule = ans?.rulesById?.get(rid)
    if (rule) {
      const s = ruleSentence(rule, ans?.restrictionVocab || null)
      // `headline` is the prose; `structured` is the declension-free read-back it falls back
      // to. ruleSentence flags `complete:false` when a Georgian case form is missing — that
      // flag governs which of the two the CAPTURE surface leads with, so this row follows the
      // same rule rather than printing a half-declined phrase as a label.
      const text = (s?.complete === false ? s?.structured : s?.headline) || s?.headline || s?.structured
      if (text) return text
    }
    return t('mv2_recorded_conditions')
  }
  const m = /^(?:policy|must|mustnot|nice):(.+)$/.exec(key)
  return ans?.vocab?.get(m ? m[1] : key)?.name || humanMatchKey(key)
}

// ── B573 RULE 2, the card's half ─────────────────────────────────────────────────────────
// *"Never render a check key; `nice:` becomes a `სასურველი` TAG not part of the name; an
// unmapped key renders an EMPTY label slot and logs."*
//
// Returns { text, tier }. `text` is '' when NOTHING can name this check — not the degraded
// underscores-to-spaces form, because in a four-column row an empty cell reads as "we have
// no name for this" while `land_area_min` reads as a translated label and hides the gap.
// The mark, the two values and the source still render, so the row keeps its information.
//
// ⚠️ The vocabulary lookup comes FIRST and is why this is not simply "blank when untranslated":
// a feature check's noun lives in the DB vocabulary (`ans.vocab`), not in the i18n dictionary,
// so `nice:has_lift` is named by a row nobody has to translate. Blanking on a missing i18n key
// alone would empty every feature row on the card.
export function cardCheckLabel(key, ans) {
  const tier = checkTier(key)
  const bare = bareCheckKey(key)
  const named = ans?.vocab?.get(/^policy:(.+)$/.exec(bare)?.[1] || bare)?.name
  if (named) return { text: named, tier }
  if (ruleIdOf(key)) return { text: conditionLabel(key, ans), tier }
  const hit = tOr(`match_key_${bare}`, null)
  if (hit) return { text: hit, tier }
  logUnmappedMatchKey(bare)
  return { text: '', tier }
}

// The tier as its OWN element, so it can never be mistaken for part of the feature's name.
// ⚠️ NO GEORGIAN WAS INVENTED (owner ruling 2026-08-08): `desk_tier_nice` is an existing
// reviewed row that already names exactly this tier, so the card and the criteria panel word
// it identically. `t()` renders the raw KEY when a row is missing, which is what made three
// invented keys silent on this very surface — reuse beats minting.
// Only `nice` is tagged: `must`/`mustnot` are the DEFAULT expectation (a requirement is
// binding unless said otherwise), and tagging every row would make the tag meaningless.
// `className` (Q5): GapRow needs the label to span the mobile row and only the mobile row.
// It defaults to '' so every other call site renders the identical class string it always did.
export function CheckLabel({ check, ans, className = '' }) {
  const { text, tier } = cardCheckLabel(check.key, ans)
  return (
    <span className={`text-xs text-foreground truncate ${className}`.trim()} title={check.detail || undefined}>
      {text}
      {tier === 'nice' && (
        <span className="ml-1 rounded px-1 py-px text-[9px] align-middle border border-blue-500/40 bg-blue-500/10 text-blue-600 dark:text-blue-400">
          {t('desk_tier_nice')}
        </span>
      )}
    </span>
  )
}
// The OWNER-FACING question for a subject — "ცხოველებს უშვებს?", not the bare noun
// "ცხოველები". 0495 shipped 35 of these as ROWS so a Georgian speaker can fix a clumsy
// question without a deploy, and two constructions are encoded in them (person groups take
// -ზე აქირავებს?, pets and household facts take -ს უშვებს?) — so this READS the column and
// never assembles a question from a name. ⚠️ Nothing selected `question_ka` from
// /api/core/features until 2026-08-08, which made "rows, not constants" quietly false and
// left this strip rendering nouns. Falls back to the noun, which is at least grammatical.
export function ownerQuestion(key, ans) {
  const m = /^(?:policy|must|mustnot):(.+)$/.exec(key)
  const row = ans?.vocab?.get(m ? m[1] : key)
  return row?.question || row?.name || humanMatchKey(key)
}

// ── RoleTag — which of the three parties a row is about ──────────────────────────────────
// Owner-picked icons (2026-08-08): owner = key_vertical, client = person_search,
// agent = real_estate_agent. Referenced through ROLE_ICON, never by glyph name, so
// re-picking one is a single edit in Icon.jsx.
//
// The icon is REDUNDANT BY DESIGN and that is the point. Amber-vs-violet already separates
// owner from client on this panel, but colour alone fails three ways an icon does not: it is
// invisible to a colour-blind reader, it survives no screenshot pasted into a chat, and it
// carries nothing at all once the row is greyed (a declined subject drops to muted, and its
// amber went with it). The word stays too — an icon alone would be a rebus.
//
// `agent` is not reachable from this column today (the answer is always the owner's or the
// client's), and is here because the same tag will label attendees and representatives, where
// three roles DO meet. One definition beats a second one written later.
export function RoleTag({ role, className = '' }) {
  const key = role === 'client' ? 'mv2_role_client' : role === 'agent' ? 'mv2_role_agent' : 'mv2_role_owner'
  return (
    <span className={`inline-flex items-center gap-1 min-w-0 ${className}`}>
      <Icon name={ROLE_ICON[role] || ROLE_ICON.owner} size={11} />
      <span className="truncate">{t(key)}</span>
    </span>
  )
}

// ═══ THE WRITE DOORS ═════════════════════════════════════════════════════════════════════
// Everything below was MOVED VERBATIM (round-2 §3): the stance/term vocabularies and
// `ownerAnswerKind` out of MatchSuggestions.jsx, and the answer context + the four controls
// out of MatchMatrix.jsx. Comments moved with their code; the only additions are this banner
// and `useAnswerDoor` at the very bottom, which is the two surfaces' shared write-runner.

// listing_policy stance cycle (0018's four values + off). Same UI grammar as the
// demand side's tier chips — tap to advance, colors carry the meaning.
export const STANCE_CYCLE = [null, 'allowed', 'preferred', 'not_allowed', 'only']
export const STANCE_STYLE = {
  allowed:     'bg-green-600/20 border-green-600 text-green-500',
  preferred:   'bg-blue-600/20 border-blue-600 text-blue-400',
  not_allowed: 'bg-red-600/20 border-red-600 text-red-400',
  only:        'bg-amber-600/20 border-amber-600 text-amber-500',
}
// TEXT marks (see TIER_ICON in MatchSuggestions) — rendered inline beside a name, never as a
// lone glyph.
export const STANCE_ICON = { allowed: '✓', preferred: '★', not_allowed: '✗', only: '❗' }

// Term gaps the owner answers yes/no — so the same 2-button UX as amenity presence applies.
// Keyed by the scorer's bare check key (pets/children/financing — see match-score.mjs
// addTermsBool + the sale block); `field` is the real listing_terms column (addTermsBool's
// delta.field is the KEY, not the column, so we map explicitly). `pos` = the green
// "good-match" answer, `neg` = red; each carries the exact column VALUE it sets — financing
// is inverted ("accepts mortgage" = cash_only:false). Terms write through
// amend_listing_terms (0055-governed, evented), per-listing.
export const TERM_CHIPS = {
  pets:      { field: 'pets_allowed',     pos: { v: true,  label: 'desk_term_allowed' },          neg: { v: false, label: 'desk_term_not_allowed' } },
  children:  { field: 'children_allowed', pos: { v: true,  label: 'desk_term_allowed' },          neg: { v: false, label: 'desk_term_not_allowed' } },
  financing: { field: 'cash_only',        pos: { v: false, label: 'desk_term_accepts_mortgage' }, neg: { v: true,  label: 'desk_term_cash_only' } },
}
// NUMERIC term gaps (2026-07-24, owner request: "occupants should be fillable like the
// others"): unlike the yes/no TERM_CHIPS, the owner's answer is a COUNT, so the chip
// opens a small number input instead of two buttons. Writes the same amend_listing_terms
// door (recordTerm) with a number value. deposit_months could join here later; occupants
// was the one the owner hit.
export const NUM_TERM_CHIPS = {
  occupants: { field: 'max_occupants', min: 1, max: 30, promptKey: 'desk_term_occupants_prompt', unitKey: 'desk_term_occupants_unit' },
}
// Which check keys the OWNER can answer, decided by the same four cases the strip's menus
// implement. Exported because the v2 panel filters its hoist list by exactly this question
// and a hand-written list of subjects would drift from the menus the moment either moved.
// Everything else — location, price, bedrooms, area — is the CLIENT's side of the row, and
// putting it in an owner strip would ask the owner to fix the client's district.
export function ownerAnswerKind(key) {
  if (key.startsWith('policy:')) return 'policy'
  const fm = /^(?:must|mustnot):(.+)$/.exec(key)
  // conditions/classes are a mutually-exclusive ladder, not a boolean amenity
  if (fm) return /^(?:class_|cls_|frame_)/.test(fm[1]) ? null : 'amenity'
  if (TERM_CHIPS[key]) return 'term'
  if (NUM_TERM_CHIPS[key]) return 'num_term'
  return null
}

// ── The answer context ───────────────────────────────────────────────────────────────────
// One fetch per listing for everything the bands need in order to say what is ALREADY
// recorded: the restriction rules (so a `rule:` check can render the owner's own sentence
// and so a client gap knows which axis it is about), the flat stances, and the terms row.
//
// It is deliberately NOT part of the match payload. The scorer consumes these to produce
// verdicts and reports verdicts; the panel wants the underlying rows so it can show
// provenance and pre-fill controls, and asking the match endpoint to serialise its inputs
// back out would make one response answer two different questions.
//
// Every branch fails SOFT: a rules fetch that 404s leaves labels degraded (generic instead of
// the sentence) and controls unchanged. Nothing here may block the matrix from rendering.
// ⚠️ `loaded` is the ONE addition to the moved code (round-2 §3), and it is load-bearing on
// the client card: that surface must render the pulsing bar in the `ვისი ჩასაწერია` cell
// until this fetch lands — *"while that fetch is out the cell carries the loading bar, not
// chips. Rendering chips optimistically and withdrawing them is the worst of the three
// options."* The initial state is otherwise indistinguishable from a listing that genuinely
// has no recorded stances, which is exactly the state the chips would misreport.
export function useListingAnswers(listingId, refreshKey) {
  const [ans, setAns] = useState(() => ({ loaded: false, vocab: new Map(), rulesById: new Map(), policyBySubject: new Map(), declined: new Set(), declinedRows: new Map() }))
  useEffect(() => {
    let gone = false
    const ka = getLanguage() === 'ka'
    Promise.all([
      getFeatures().catch(() => []),
      listingId ? listRestrictionRules(listingId).catch(() => null) : null,
      listingId ? getRestrictionVocab().catch(() => null) : null,
      listingId ? listListingPolicies(listingId).catch(() => null) : null,
      listingId ? getListingTerms(listingId).catch(() => null) : null,
    ]).then(([fs, rules, rvocab, pols, terms]) => {
      if (gone) return
      setAns({
        loaded: true,
        vocab: new Map((fs || []).map(f => [f.code, {
          name: (ka ? (f.name_ka || f.name_en) : (f.name_en || f.name_ka)) || f.code,
          question: (ka ? (f.question_ka || f.question_en) : (f.question_en || f.question_ka)) || null,
        }])),
        rulesById: new Map((rules?.rules || []).filter(r => r?.id).map(r => [String(r.id), r])),
        restrictionVocab: rvocab || null,
        policyBySubject: new Map((pols?.policies || []).filter(p => p?.subject).map(p => [p.subject, p])),
        terms: terms?.terms || null,
        // B586 — subjects the owner was asked about and would not answer. Rides on the
        // policies response, so this costs no extra request. An older server omits the key
        // and every subject stays askable, which is the pre-B586 behaviour: the feature
        // degrades to "keep asking", never to "silently stop".
        declined: new Set((pols?.declined || []).map(d => d?.subject).filter(Boolean)),
        declinedRows: new Map((pols?.declined || []).filter(d => d?.subject).map(d => [d.subject, d])),
      })
    })
    return () => { gone = true }
  }, [listingId, refreshKey])
  return ans
}

// ── B586: which SUBJECT a check's decline is recorded against ────────────────────────────
// The questions an owner can decline are not one vocabulary — `pol_*` feature codes and
// `listing_terms` column names both appear — and 0508's gateway validates against exactly
// those two spaces. This is the one place that maps a check key into them, derived from the
// SAME specs that write the answer (TERM_CHIPS / NUM_TERM_CHIPS), so a decline can never be
// filed against a subject the answer would not have written to.
export function declineSubjectOf(checkKey) {
  const kind = ownerAnswerKind(checkKey)
  if (kind === 'policy') return checkKey.slice(7)
  if (kind === 'amenity') return /^(?:must|mustnot):(.+)$/.exec(checkKey)?.[1] || null
  if (kind === 'term') return TERM_CHIPS[checkKey]?.field || null
  if (kind === 'num_term') return NUM_TERM_CHIPS[checkKey]?.field || null
  return null
}
export const isDeclined = (checkKey, ans) => {
  const s = declineSubjectOf(checkKey)
  return !!(s && ans?.declined?.has(s))
}

// ── AnswerPills — ONE dashed group, not four filled chips (design, 2026-08-08) ───────────
// The redline draws every answer control the same way: a single DASHED outline around the
// whole option set, with the options inside as bare coloured glyphs. Four individually
// bordered and tinted chips — what shipped first — reads as four separate things that are
// each already ON, so a row of unanswered options looked like a row of recorded answers.
//
// The dash carries the meaning: **dashed = nobody has answered this yet**, the same signal
// `PILL_ADD` uses across the Desk. Answering fills the chosen option and turns the group
// SOLID, so a glance down the strip separates asked-and-answered from still-open without
// reading a word. That is the spec's own rule ("Unset subject: dashed border. Set: solid,
// the chosen stance filled") and it only works if the unset state is genuinely quiet.
//
// `value` is the recorded answer when there is one — read from the listing's own rows, so
// the control shows what the terms panel shows rather than resetting to blank on every open.
//
// ── Round-2 §2: on a phone, FOUR EQUAL TARGETS ───────────────────────────────────────────
// Below 640px the group fills its line and every option takes `flex-1` at a 36px minimum,
// glyph only, with `title` carrying the word. Equal width IS the statement: the system has
// no opinion about what the owner will say, and the earlier plan — widen and label the
// "likely" answer — was a layout instinct dressed as a prediction the data does not carry.
// So no option may ever be widened; if labels come, they come to all four or to none.
// ⚠️ The desk is untouched: `sm:` restores `h-6 min-w-[1.5rem]` and `flex-initial` (which is
// `0 1 auto`, the flex-item default this button had before — `flex-none` would NOT be, it
// stops the shrink these four rely on inside a 118px cell).
// ⚠️ This control is shared with the client card's fourth column, and that is intended:
// both surfaces get 36px targets on a phone from one definition.
function AnswerPills({ options, value = null, busy = false, onPick, className = '' }) {
  const answered = value != null
  return (
    /* SCROLLABLE, on the owner's own suggestion (2026-08-19: *"we may have this pills
       scrollable"*). The group is variable-length — B586's `არ მითხრა` is a fifth option on
       every declinable subject, and a recorded count above the quick range adds a sixth —
       and the widest label is a two-word Georgian phrase. Below a certain width something
       has to give, and the three candidates are: wrap the labels (the pill grows two lines
       tall and the row jumps), squeeze them (a 20px pill nobody can read or hit), or scroll.
       Scrolling is the only one that keeps every option BOTH legible and reachable.
       How the two states compose: `flex-1` still spreads the pills evenly whenever they fit,
       because a flex item's automatic minimum is its min-content size — with `whitespace-
       nowrap` that is the label's full width, so they refuse to shrink below readable and the
       strip overflows into its own scroller instead. Nothing changes for the common 2–4
       option case; only the crowded one stops clipping.
       ⚠️ `overflow-x-auto` needs a real width to scroll INSIDE — see the `justify-self-stretch`
       note on GapRow, which is the other half of this fix. Restoring `justify-self-start`
       there brings the clipping straight back. */
    <span className={`flex w-full overflow-x-auto sm:overflow-visible sm:inline-flex sm:w-auto items-center gap-0.5 rounded-full border px-1 py-0.5 ${
      answered ? 'border-border' : 'border-dashed border-border/70'} ${className}`}>
      {options.map(o => {
        const on = answered && o.value === value
        return (
          <button key={o.key ?? String(o.value)} type="button" disabled={!!busy} title={o.title}
            onClick={() => onPick(o.value)}
            className={`flex-1 min-h-[36px] px-1.5 rounded-full text-xs inline-flex items-center whitespace-nowrap
              sm:flex-initial sm:h-6 sm:min-h-0 sm:min-w-[1.5rem]
              justify-center disabled:opacity-40 transition-colors
              ${on ? o.onCls : `${o.cls} hover:bg-accent/50`}`}>
            {o.label}
          </button>
        )
      })}
    </span>
  )
}
// The four stances as pill options. Colour lives on the GLYPH when unset and on the whole
// chip when chosen — STANCE_STYLE is the recorded-answer look, reused rather than restated.
const STANCE_GLYPH_CLS = {
  allowed: 'text-emerald-500', preferred: 'text-sky-400',
  not_allowed: 'text-red-400', only: 'text-amber-500',
}
const stancePillOptions = () => STANCE_CYCLE.filter(Boolean).map(st => ({
  key: st, value: st, label: STANCE_ICON[st], title: t(`desk_stance_${st}`),
  cls: STANCE_GLYPH_CLS[st], onCls: STANCE_STYLE[st],
}))
// B586 — the fourth option the mock always drew and nothing could store until core 0508.
// `value: DECLINED` is a SENTINEL, not a stance: it never reaches listing_policy, and the
// caller branches on it to hit the decline door instead. Rendered muted rather than coloured
// because it is not an answer about the flat — it is the end of a phone call.
const DECLINED = Symbol('declined')
const declinePillOption = () => ({
  key: 'declined', value: DECLINED, label: t('mv2_wouldnt_say'), title: t('mv2_wouldnt_say'),
  cls: 'text-muted-foreground/70', onCls: 'bg-muted text-muted-foreground',
})
// A boolean answer is WORDED, not glyphed: კი / არა. A bare ✓/✗ beside a question reads as
// a status mark rather than as the two answers to it.
const boolPillOptions = (posTitle, negTitle) => [
  { key: 'y', value: true, label: t('mv2_yes'), title: posTitle,
    cls: 'text-emerald-500', onCls: 'bg-green-600/20 text-green-500' },
  { key: 'n', value: false, label: t('mv2_no'), title: negTitle,
    cls: 'text-red-400', onCls: 'bg-red-600/20 text-red-400' },
]

// ── The owner's answer, inline ───────────────────────────────────────────────────────────
// The same three doors HoistedStrip opens (stance / feature presence / term), rendered as a
// row of buttons instead of a popover because inside a band there is room for them and a
// popover would cover the very rows the agent is reading. `ownerAnswerKind` decides which
// door, so the strip and the band can never disagree about what is answerable.
//
// `ans` is optional and only ever ADDS: with it the group can show the recorded answer as
// selected; without it every group renders unset, which is the pre-2026-08-08 behaviour.
export function OwnerFill({ checkKey, busy, canRecord, listingId, propertyId, agentId, onRun, ans = null }) {
  const kind = canRecord ? ownerAnswerKind(checkKey) : null
  const fm = /^(?:must|mustnot):(.+)$/.exec(checkKey)
  const declined = isDeclined(checkKey, ans)
  const subj = declineSubjectOf(checkKey)
  // B586. Every group carries the option, and one branch handles it for all four kinds:
  // `DECLINED` is a sentinel, so picking it goes to the decline door and NOT to the door
  // that records a value. Tapping it again retracts — the owner may answer later, and a
  // one-way control would make a mis-tap permanent.
  const withDecline = (opts) => (listingId && subj ? [...opts, declinePillOption()] : opts)
  const pick = (fn) => (v) => v === DECLINED
    ? onRun(checkKey, () => setAnswerDeclined(listingId, subj, !declined, { agentId }))
    : fn(v)
  const declinedValue = declined ? DECLINED : undefined

  if (kind === 'policy' && listingId) {
    const subject = checkKey.slice(7)
    return (
      <AnswerPills options={withDecline(stancePillOptions())} busy={busy}
        value={declinedValue ?? (ans?.policyBySubject?.get(subject)?.stance ?? null)}
        onPick={pick(st => onRun(checkKey, () => setListingPolicy(listingId, subject, st, null, agentId)))} />
    )
  }
  if (kind === 'amenity' && fm) {
    return (
      <AnswerPills options={withDecline(boolPillOptions(t('desk_feature_has'), t('desk_feature_absent')))}
        busy={busy} value={declinedValue ?? null}
        onPick={pick(v => onRun(checkKey, () => setPropertyFeature(propertyId, fm[1], v, agentId)))} />
    )
  }
  if (kind === 'term' && listingId) {
    const spec = TERM_CHIPS[checkKey]
    // The recorded value is the TERMS field, mapped back through the same chip spec that
    // writes it — never a second hand-written {pets: 'pets_allowed'} map.
    const cur = ans?.terms ? ans.terms[spec.field] : null
    const value = cur == null ? null : cur === spec.pos.v ? true : cur === spec.neg.v ? false : null
    return (
      <AnswerPills options={withDecline(boolPillOptions(t(spec.pos.label), t(spec.neg.label)))}
        busy={busy} value={declinedValue ?? value}
        onPick={pick(v => onRun(checkKey, () => amendListingTerms(listingId,
          [{ op: 'set', field: spec.field, value: v ? spec.pos.v : spec.neg.v }], agentId)))} />
    )
  }
  if (kind === 'num_term' && listingId) {
    const spec = NUM_TERM_CHIPS[checkKey]
    return <NumPills spec={spec} busy={busy} value={declinedValue ?? (ans?.terms?.[spec.field] ?? null)}
      extraOptions={subj ? [declinePillOption()] : []}
      onPick={pick(n => onRun(checkKey, () => amendListingTerms(listingId, [{ op: 'set', field: spec.field, value: n }], agentId)))} />
  }
  // Not answerable in place — an amenity ladder rung, or the viewer is not the rep. The row
  // still belongs here (it is still the call to make); it renders WITHOUT controls rather
  // than with buttons that would refuse. A control a viewer cannot use is the 2026-08-06
  // "lock naming an action they do not have" defect.
  return null
}

// ── NumPills — the count, as taps ────────────────────────────────────────────────────────
// The mock draws `1 | 2 | 3+ | არ მითხრა`, and TWO of those four cannot be stored honestly:
//
//   • `3+` would write `max_occupants = 3`. "Three or more" is not three — it is unknown
//     above three — and storing the lower bound as the ceiling makes the scorer HARD-FAIL a
//     four-person client on a flat whose owner said they were fine. A wrong number is worse
//     than the blank it replaces, because a blank is visibly a gap and a number is not.
//   • `არ მითხრა` ("wouldn't say") has nowhere to go at all: `listing_terms` can hold a
//     count or a null, and null already means "nobody asked". Recording *asked and refused*
//     is a real and useful distinction — it is what stops the strip asking forever — but it
//     needs a column that does not exist yet. Rendering the button now would be a control
//     that silently does nothing, which is the 2026-08-06 lock-with-no-action defect.
//
// So the group is `1 | 2 | 3 | +`: three exact one-tap answers and a door to the input for
// everything above. Same dashed shell, same selected-fills behaviour. See the backlog row
// for the `wouldn't say` store; the moment it exists this is one more option in the array.
function NumPills({ spec, busy, value = null, onPick, extraOptions = [] }) {
  const [open, setOpen] = useState(false)
  const quick = [1, 2, 3].map(n => ({
    key: String(n), value: n, label: String(n), title: t(spec.promptKey),
    cls: 'text-muted-foreground', onCls: 'bg-accent text-foreground',
  }))
  // A recorded value above the quick range still SHOWS — as its own selected pill — rather
  // than leaving the group looking unanswered because 7 is not 1, 2 or 3.
  const options = value != null && value > 3
    ? [...quick, { key: 'cur', value, label: String(value), title: t(spec.promptKey),
        cls: 'text-muted-foreground', onCls: 'bg-accent text-foreground' }]
    : quick
  if (open) {
    return <InlineNumber min={spec.min} max={spec.max} unitKey={spec.unitKey || 'mv2_people'}
      busy={busy} onSave={n => { setOpen(false); onPick(n) }} />
  }
  return (
    <span className="inline-flex items-center gap-1">
      <AnswerPills options={[...options, ...extraOptions]} value={value} busy={busy} onPick={onPick} />
      {/* The input door is only offered while the question is still OPEN. Once a count is
          recorded — or the owner has declined — a ＋ that could write a number nobody gave is
          exactly the affordance B586 exists to remove. Correcting a recorded value goes
          through the terms panel, which shows the whole row rather than one field. */}
      {value == null && (
        <button type="button" disabled={!!busy} title={t(spec.promptKey)} onClick={() => setOpen(true)}
          className="h-6 w-6 rounded-full border border-dashed border-border/70 text-xs text-muted-foreground
            hover:bg-accent/50 disabled:opacity-40 inline-flex items-center justify-center">＋</button>
      )}
    </span>
  )
}

// Shared numeric entry — owner counts (max_occupants) and client counts (occupants) take the
// same shape, and the design draws them the same way.
export function InlineNumber({ min, max, unitKey, busy, onSave, placeholder = '' }) {
  const [val, setVal] = useState('')
  const commit = () => { const n = Number(val); if (Number.isFinite(n) && n >= min && n <= max) onSave(n) }
  return (
    <span className="flex items-center gap-1">
      <input type="number" min={min} max={max} value={val} disabled={!!busy} placeholder={placeholder}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit() } }}
        className="w-12 px-1 py-0.5 rounded border border-border bg-background text-xs disabled:opacity-50" />
      {unitKey && <span className="text-xs text-muted-foreground">{t(unitKey)}</span>}
      <button type="button" disabled={!!busy || val === ''} onClick={commit}
        className="w-6 h-6 rounded-md border text-xs inline-flex items-center justify-center disabled:opacity-40 bg-green-600/20 border-green-600 text-green-500">✓</button>
    </span>
  )
}

// ── The client's answer, inline ──────────────────────────────────────────────────────────
// The half that did not exist. Owner ruling 2026-08-08: *"when client says they have no pet
// then we know it's no restraint, but if client has not specified pet then we should ask
// about pet if owner has restriction on that."*
//
// That question is already produced — match-score's restriction-rules block emits it as a
// `household_gap` with `actions:['ask_client']` — and the panel could neither route it (the
// prose regex sent it to the owner) nor answer it (there was no client-side write on this
// surface at all). This is that write, through the SAME door the demand editor uses:
// `amend_requirement` via execute_proposal. Nothing new is invented, and the provenance of a
// value filled here is identical to one typed into the client's own form.
//
// ⚠️ A `false` is a REAL ANSWER and must be recordable, which is the whole point of the
// ruling: "no pets" ends the question, silence does not. So the boolean control has two
// buttons, not a checkbox that can only ever be turned on.
//
// ⚠️ `m` is a CONTEXT, not the match row. It needs exactly two facts — which requirement is
// being amended and whose party it belongs to — and the client surface holds both without
// holding a match row at all (its rows are properties). Round-2 §3 passes a two-field object
// built from `completeness.fill`; the matrix passes its row, which already has both fields.
export function ClientFill({ check, fill, m, agentId, busy, onRun, onOpenDemand }) {
  const set = (value) => onRun(check.key, () => commitIntakeEnvelope({
    raw_text: `[match panel client fill] requirement ${m.requirement_id} — ${fill.field}`,
    channel: 'scheduler',
    envelope: { decision: 'noop', confidence: 1, commands: [{
      type: 'amend_requirement', target_requirement_id: m.requirement_id,
      person_party_id: m.person?.party_id || null,
      ops: [{ field: fill.field, op: value === null ? 'clear' : 'set', value }],
      human_edited: true,
    }] },
    anchors: null, intake_id: null, agent_id: agentId,
  }).then(resp => {
    // execute_proposal reports per-command execution; a refusal here is silent otherwise and
    // the row would just sit there looking answered until the next reload put it back.
    if ((resp?.results || []).some(r => !r.executed)) throw new Error('refused')
  }))
  // A multi-select (nationality, profession, districts) has no honest one-tap form: the
  // panel would have to guess which `pol_*` code an axis value maps to. It points at the
  // demand editor instead — a door that works beats a control that guesses.
  if (fill.editor || fill.kind === 'codes') {
    return (
      <button type="button" onClick={() => onOpenDemand?.(m.requirement_id)}
        className={pill('add', { door: true, sm: true, extra: 'h-[22px] text-xs' })}>
        ＋ {t('mv2_fill_in')}
      </button>
    )
  }
  // Same dashed group as the owner's side. The two sides differ in COLOUR everywhere else on
  // this panel (amber vs violet) but not here: the control is the same act — record an answer
  // — and giving it two shapes would suggest the client's answer is a different kind of thing.
  if (fill.kind === 'bool') {
    return (
      <AnswerPills busy={busy} onPick={set}
        options={boolPillOptions(t('desk_term_allowed'), t('desk_term_not_allowed'))} />
    )
  }
  if (fill.kind === 'number') {
    return <InlineNumber min={fill.min} max={fill.max} busy={busy} onSave={set} />
  }
  if (fill.kind === 'date') {
    return <InlineDate busy={busy} onSave={set} />
  }
  return null
}

function InlineDate({ busy, onSave }) {
  const [val, setVal] = useState('')
  return (
    <span className="flex items-center gap-1">
      <DateInput value={val} disabled={!!busy} onChange={v => setVal(v)}
        className="flex items-center gap-0.5 px-1 py-0.5 rounded border border-border bg-background text-xs disabled:opacity-50" />
      <button type="button" disabled={!!busy || !val} onClick={() => val && onSave(val)}
        className="w-6 h-6 rounded-md border text-xs inline-flex items-center justify-center disabled:opacity-40 bg-green-600/20 border-green-600 text-green-500">✓</button>
    </span>
  )
}

// "დღეს რა ვიცით" for a CLIENT-side gap — what the demand already tells us about this
// condition, so the agent can see whether they are confirming or asking cold.
//
// ⚠️ The endpoint's `demand` digest carries four facts (category, districts, budget, rooms)
// and the client-side gaps are mostly the other ones — occupants, move-in, pets, children.
// So this is EMPTY on most rows today, and empty is the honest answer: the row is a gap
// precisely because nobody has recorded it. The mock's "კლიენტი: 2 ადამიანი" needs
// `occupants` and `move_in_date` added to the digest in core_match.js — a serialisation
// change, not a new query, since the criteria row is already loaded there.
export function knownFromDemand(check, m) {
  const asked = askedOf(check, m)
  return asked || ''
}

// ── useAnswerDoor — the write-runner both surfaces open (round-2 §3) ─────────────────────
// The matrix has always owned this as four loose pieces: `useListingAnswers(listingId, …)`
// for what is already recorded, an `answerKey` that re-reads it, one `fillBusy` lock so two
// controls can never be mid-flight against the same listing, and a `runFill` that awaits the
// write and then re-scores. The client card needs all four and must not grow a second copy
// of them, so they are one hook.
//
// ⚠️ `onAnswered` is how the RE-SCORE stays with the caller, and it has to: the matrix
// re-fetches `matching-clients`, the client card re-fetches its own requirement matches, and
// neither knows how to do the other's. The hook only knows that after a successful write the
// recorded-answers read is stale — that part it does itself (`answerKey`), because it would
// otherwise be four call sites remembering the same thing.
//
// ⚠️ ONE FETCH PER MOUNT, and that is the whole reason the client card calls this from a
// per-expansion component: `useListingAnswers` issues four requests for its `listingId`, so
// calling it once per row would be 4 × 50 requests on open for cards nobody expanded. The
// expansion IS the consent to pay for them (round-2 §3: "the fetch follows the expansion").
//
// `refreshKey` is optional and only exists so the matrix keeps the behaviour it already had:
// its panel-level refreshKey re-reads the listing's rows as well as the match payload.
export function useAnswerDoor({ listingId, propertyId, agentId, onAnswered, refreshKey = 0 }) {
  const [answerKey, setAnswerKey] = useState(0)
  const [fillBusy, setFillBusy] = useState(null)
  const [fillErr, setFillErr] = useState(null)
  const ans = useListingAnswers(listingId, `${refreshKey}:${answerKey}`)
  // One write lock for every inline fill on the panel — owner stances, feature presence,
  // terms and the client's own criteria all go through it, so two controls can never be
  // mid-flight against the same row. Every door ends in `onAnswered()`, which is what makes
  // the strip's "the list recounts as soon as you record an answer" promise true rather than
  // decorative.
  const runFill = async (key, fn) => {
    if (fillBusy) return
    setFillBusy(key)
    setFillErr(null)
    try {
      await fn()
      // A recorded answer changes what the bands can SHOW, not only what the scorer computes
      // — the provenance line, the answered rows, the pre-filled value. Re-scoring alone
      // would leave a just-answered subject rendering as though nobody had asked.
      setAnswerKey(k => k + 1)
      await onAnswered?.()
    }
    catch (e) { setFillErr(e.message) }
    finally { setFillBusy(null) }
  }
  return { ans, runFill, fillBusy, fillErr, answerKey, propertyId, agentId }
}
